// OWNER: AREA-TEMPLATES (templates/*, partials/*). Components go in src/features/templates.
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Layers, Plus, Puzzle, Search, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { PartialSummary } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { Tooltip } from '@/components/ui/tooltip'
import { ownerFilters, usePartialsList, type OwnerView } from '@/features/templates/api'
import { OwnerLabel } from '@/features/templates/bits'
import { overrideMap, type OverrideInfo } from '@/features/templates/overrides'
import { CreatePartialDialog } from '@/features/templates/partials/CreatePartialDialog'
import { ProposalCountBadge } from '@/features/proposals/ProposalCountBadge'
import { useHotkeys } from '@/lib/hotkeys'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useAppScope } from '@/shell/useAppScope'

interface PartialsSearch {
  q?: string
  owner?: OwnerView
}

export const Route = createFileRoute('/partials/')({
  validateSearch: (s: Record<string, unknown>): PartialsSearch => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    owner: s.owner === 'app' || s.owner === 'shared' ? s.owner : undefined,
  }),
  component: PartialsPage,
})

type Row = PartialSummary & { override: OverrideInfo | undefined }

const col = tableColumns<Row>()
const columns = col.columns([
  col.accessor('key', {
    header: 'Partial',
    cell: (c) => (
      <div className="min-w-0">
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate font-mono text-sm font-medium text-ink">{c.getValue()}</span>
          <ProposalCountBadge
            count={c.row.original.open_proposals}
            to={{ kind: 'partial', id: c.row.original.id }}
          />
        </div>
        <div className="truncate text-xs text-ink-2">
          {c.row.original.description || <span className="text-ink-3">No description</span>}
        </div>
      </div>
    ),
    enableHiding: false,
  }),
  col.accessor((p) => p.application_id ?? '', {
    id: 'owner',
    header: 'Owner',
    cell: (c) => <OwnerLabel applicationId={c.row.original.application_id} className="text-sm" />,
    meta: { width: '13rem', label: 'Owner' },
  }),
  col.accessor((p) => (p.override?.overrides ? 1 : (p.override?.overriddenBy.length ?? 0)), {
    id: 'precedence',
    header: 'Precedence',
    cell: (c) => {
      const o = c.row.original.override
      if (o?.overrides)
        return (
          <Tooltip content="This application’s templates use this partial instead of the shared one with the same key.">
            <Badge tone="brass" tabIndex={0}>
              <Layers /> Overrides shared
            </Badge>
          </Tooltip>
        )
      if (o && o.overriddenBy.length > 0)
        return (
          <Tooltip content="Some applications have their own partial with this key, which their templates use instead.">
            <Badge tone="neutral" tabIndex={0}>
              Overridden in {o.overriddenBy.length}{' '}
              {o.overriddenBy.length === 1 ? 'application' : 'applications'}
            </Badge>
          </Tooltip>
        )
      return <span className="text-ink-3">—</span>
    },
    meta: { width: '13rem' },
  }),
  col.accessor((p) => p.content.length, {
    id: 'size',
    header: 'Size',
    cell: (c) => <span className="tabular text-ink-2">{c.getValue().toLocaleString()} chars</span>,
    meta: { align: 'right', width: '7rem' },
  }),
  col.accessor('updated_at', {
    header: 'Updated',
    cell: (c) => (
      <div className="text-right">
        <RelativeTime value={c.getValue()} className="text-ink-2" />
        <div className="truncate text-xs text-ink-3">{c.row.original.updated_by}</div>
      </div>
    ),
    sortFn: 'datetime',
    meta: { align: 'right', width: '10rem' },
  }),
])

function PartialsPage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = Route.useSearch()
  const { appId, application } = useAppScope()
  const { can } = usePermissions()
  const [text, setText] = useState(search.q ?? '')
  const q = useDebouncedValue(text.trim(), 250)
  const [creating, setCreating] = useState(false)
  const view: OwnerView = search.owner === 'app' && !appId ? 'all' : (search.owner ?? 'all')
  const list = usePartialsList({ ...ownerFilters(view, appId), q: q || undefined })
  const rows = useMemo<Row[]>(() => {
    const map = overrideMap(list.items)
    return list.items.map((p) => ({ ...p, override: map.get(p.id) }))
  }, [list.items])

  useEffect(() => {
    if ((search.q ?? '') !== q)
      void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true })
  }, [search.q, q, navigate])
  useHotkeys([{ keys: 'c', handler: () => can('edit_content') && setCreating(true) }])

  const ownerOptions = [
    { value: 'all' as const, label: application ? `${application.name} and shared` : 'All owners' },
    ...(appId
      ? [{ value: 'app' as const, label: `${application?.name ?? 'This application'} only` }]
      : []),
    { value: 'shared' as const, label: 'Shared library' },
  ]
  const createButton = (
    <Button variant="primary" icon={<Plus />} onClick={() => setCreating(true)}>
      New partial
    </Button>
  )

  return (
    <>
      <PageHeader
        title="Partials"
        description={
          <>
            Reusable layouts and snippets, used as <code className="font-mono">{'{{> key}}'}</code>{' '}
            or <code className="font-mono">{'{{#> layout}}…{{/layout}}'}</code>. An application’s
            partial overrides a shared partial with the same key.
          </>
        }
        actions={
          <Can permission="edit_content" mode="disable">
            {createButton}
          </Can>
        }
      />
      <DataTable
        aria-label="Partials"
        columns={columns}
        data={rows}
        getRowId={(p) => p.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={list.pagination}
        persistKey="partials"
        initialSorting={[{ id: 'key', desc: false }]}
        onRowClick={(p) =>
          void navigate({ to: '/partials/$partialId', params: { partialId: p.id } })
        }
        toolbar={
          <div className="flex w-full flex-wrap items-center gap-2">
            <Input
              className="w-full sm:w-64"
              size="sm"
              placeholder="Search by key or description"
              aria-label="Search partials"
              value={text}
              onChange={(e) => setText(e.target.value)}
              leading={<Search />}
              trailing={
                text ? (
                  <IconButton
                    label="Clear search"
                    size="xs"
                    tooltip={false}
                    onClick={() => setText('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <Select<OwnerView>
              size="sm"
              className="w-full sm:w-56"
              aria-label="Owner"
              value={view}
              onValueChange={(v) =>
                void navigate({
                  search: (prev) => ({ ...prev, owner: v === 'all' ? undefined : v }),
                  replace: true,
                })
              }
              options={ownerOptions}
            />
          </div>
        }
        empty={
          q || view !== 'all' ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching partials"
              description={q ? `Nothing matches “${q}”.` : 'No partials match this filter.'}
            />
          ) : (
            <EmptyState
              icon={Puzzle}
              title="No partials yet"
              description="Put the shared layout, header, footer, and buttons in partials so every template stays consistent."
              action={can('edit_content') ? createButton : undefined}
            />
          )
        }
      />
      <CreatePartialDialog open={creating} onOpenChange={setCreating} defaultOwner={appId} />
    </>
  )
}
